import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { useUser } from '@clerk/clerk-react'
import { BrandedSpinner } from '@/components/BrandedSpinner'
import { useScamCheckImageMutation, useScamCheckMutation, useScamCheckVideoMutation } from '@/hooks/useScamCheck'
import { getUserFromClerkId } from '@/hooks/useUser'
import { clearPendingScamCheck, getPendingScamCheck, type PendingScamCheckRequest } from '@/lib/scamCheckPending'
import { FiArrowLeft } from 'react-icons/fi'

/**
 * Turn a failed check into a message the user can act on. The API's 4xx
 * bodies are not always JSON, so map by status first and only peek at the
 * body for "too long / too large". Mirrors the ScamDefense partner checker.
 */
function checkErrorMessage(e: unknown, kind: 'text' | 'file'): string {
  const err = e as { code?: string; response?: { status: number; data?: unknown } }
  const generic = kind === 'file'
    ? 'We couldn’t analyze that file just now. Please try again in a moment.'
    : 'We couldn’t run the check just now. Please try again in a moment.'
  const res = err?.response
  if (!res) {
    return err?.code === 'ECONNABORTED'
      ? 'The check took too long to finish. Please try again. Until you get a result, don’t act on the message.'
      : generic
  }
  const body = typeof res.data === 'string' ? res.data : JSON.stringify(res.data ?? '')
  if (res.status === 429) return 'You’ve made a lot of checks in a short time. Give it a few minutes and try again.'
  if (res.status === 413 || /too (long|large|big)|exceeds|max(imum)? (length|size)/i.test(body)) {
    return kind === 'file'
      ? 'That file is too large to check. Pictures must be under 10MB and videos under 25MB.'
      : 'That’s too long to check in one go. Paste the part with the link, or the part asking for money or details.'
  }
  const wrongType = 'Please choose a JPEG, PNG or WebP picture, or an MP4 or WebM video.'
  if (res.status === 415 || /unsupported|file type|mime/i.test(body)) return wrongType
  if (res.status === 408 || res.status === 504) {
    return 'The check took too long to finish. Please try again. Until you get a result, don’t act on the message.'
  }
  if (res.status >= 400 && res.status < 500) {
    return kind === 'file'
      ? wrongType
      : 'We couldn’t check that. Try pasting just the link, or the message text exactly as you received it.'
  }
  return generic
}

type StepDefinition = {
  title: string
  subtitle: string
}

function getSteps(mode: PendingScamCheckRequest['mode'], british: boolean): StepDefinition[] {
  const s = (us: string) => british ? us.replace(/iz/g, 'is') : us
  const map: Record<PendingScamCheckRequest['mode'], StepDefinition[]> = {
    image: [
      { title: 'Preparing image', subtitle: `${s('Optimizing')} your upload for analysis.` },
      { title: 'Reading visual cues', subtitle: 'Extracting text, logos, and context.' },
      { title: 'Checking for impersonation', subtitle: 'Comparing against known brand patterns.' },
      { title: 'Building your report', subtitle: 'Preparing your verdict and next steps.' },
    ],
    link: [
      { title: 'Visiting the page', subtitle: 'Safely loading the website content.' },
      { title: 'Scanning threat databases', subtitle: 'Checking known scam and phishing lists.' },
      { title: `${s('Analyzing')} page content`, subtitle: 'Looking for manipulation and pressure tactics.' },
      { title: 'Building your report', subtitle: `${s('Summarizing')} findings and next steps.` },
    ],
    message: [
      { title: 'Reading message', subtitle: 'Extracting text patterns and signals.' },
      { title: 'Checking scam indicators', subtitle: 'Looking for coercion, urgency, and pressure.' },
      { title: 'Scoring confidence', subtitle: 'Combining context with known scam patterns.' },
      { title: 'Building your report', subtitle: 'Turning findings into clear next steps.' },
    ],
    video: [
      { title: 'Uploading video', subtitle: 'Securely transferring your video for analysis.' },
      { title: 'Listening to the audio', subtitle: 'Transcribing everything that is said.' },
      { title: 'Reading the script', subtitle: `${s('Analyzing')} what the video says and asks you to do.` },
      { title: 'Checking for manipulation patterns', subtitle: 'Comparing against known AI video scam tactics.' },
      { title: 'Building your report', subtitle: `${s('Summarizing')} findings and next steps.` },
    ],
  }
  return map[mode] || map.link
}

// Staggered timing: each step takes slightly longer to feel natural.
// Video analysis genuinely takes longer (upload + transcription + vision),
// so its steps pace slower to stay honest with the real work happening.
const STEP_TIMINGS: Partial<Record<PendingScamCheckRequest['mode'], number[]>> = {
  video: [3000, 6000, 8000, 8000, 5000],
}
const DEFAULT_STEP_TIMINGS = [1200, 2000, 2500, 2000]

// SVG animated checkmark — draws the circle then the tick
function AnimatedCheck() {
  return (
    <svg viewBox="0 0 36 36" className="h-9 w-9">
      <circle
        cx="18" cy="18" r="16"
        fill="none"
        stroke="#d1fae5"
        strokeWidth="2.5"
      />
      <circle
        cx="18" cy="18" r="16"
        fill="none"
        stroke="#10b981"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="100.53"
        strokeDashoffset="100.53"
        style={{ animation: 'draw-circle 0.4s ease-out forwards' }}
      />
      <path
        d="M12 18.5 l4.5 4.5 l8 -8.5"
        fill="none"
        stroke="#10b981"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="20"
        strokeDashoffset="20"
        style={{ animation: 'draw-check 0.3s ease-out 0.35s forwards' }}
      />
      <style>{`
        @keyframes draw-circle {
          to { stroke-dashoffset: 0; }
        }
        @keyframes draw-check {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </svg>
  )
}

// Video preview with a looping "scanning" sweep — the video plays muted while
// the beam and viewfinder corners signal that analysis is happening on it.
// The wrapper shrink-wraps the video (w-fit + block), so the overlay and
// rounded corners clip to the video's real dimensions, never a letterbox.
function VideoScanPreview({ src }: { src: string }) {
  return (
    <div className="relative mx-auto w-fit max-w-full overflow-hidden rounded-2xl">
      <video
        src={src}
        muted
        loop
        autoPlay
        playsInline
        className="block max-h-[240px] max-w-full"
      />

      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {/* Scanning beam: a bright edge with a soft trailing glow, sweeping down.
            Positioned via percentage `top` so it works at any video height. */}
        <div
          className="absolute inset-x-0 h-[45%]"
          style={{
            background: 'linear-gradient(to bottom, rgba(139,92,246,0) 0%, rgba(139,92,246,0.10) 45%, rgba(167,139,250,0.28) 80%, rgba(196,181,253,0.85) 98%, rgba(237,233,254,0.95) 100%)',
            animation: 'scan-sweep 2.6s cubic-bezier(0.45, 0, 0.55, 1) infinite',
          }}
        />

        {/* Viewfinder corner brackets */}
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* vector-effect keeps the stroke width constant despite the stretched viewBox */}
          <g stroke="rgba(255,255,255,0.75)" strokeWidth="2.5" strokeLinecap="round" fill="none" vectorEffect="non-scaling-stroke">
            <path d="M 4 12 L 4 5 L 10 5" vectorEffect="non-scaling-stroke" />
            <path d="M 90 5 L 96 5 L 96 12" vectorEffect="non-scaling-stroke" />
            <path d="M 96 88 L 96 95 L 90 95" vectorEffect="non-scaling-stroke" />
            <path d="M 10 95 L 4 95 L 4 88" vectorEffect="non-scaling-stroke" />
          </g>
        </svg>
      </div>

      <style>{`
        @keyframes scan-sweep {
          0% { top: -45%; opacity: 0; }
          12% { opacity: 1; }
          88% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  )
}

// Spinning gear icon for active steps
function SpinningGear() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7 animate-spin text-violet-500" style={{ animationDuration: '2.5s' }}>
      <path
        fill="currentColor"
        d="M12 15.5A3.5 3.5 0 0 1 8.5 12 3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97s-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1s.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.58 1.69-.98l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66Z"
      />
    </svg>
  )
}

export const Route = createFileRoute('/_auth/tools/scam-check/processing')({
  validateSearch: (search: Record<string, unknown>) => ({
    requestId: typeof search.requestId === 'string' ? search.requestId : '',
    invitationcode: typeof search.invitationcode === 'string' ? search.invitationcode : undefined,
    __clerk_ticket: typeof search.__clerk_ticket === 'string' ? search.__clerk_ticket : undefined,
  }),
  component: ScamCheckProcessingPage,
})

function ScamCheckProcessingPage() {
  const router = useRouter()
  const { requestId } = Route.useSearch()
  const { user: clerkUser } = useUser()
  const { data: appUser } = getUserFromClerkId(clerkUser?.id ?? '', { enabled: !!clerkUser })
  const useBritishSpelling = appUser?.market === 'GB' || appUser?.market === 'AU'
  const mutation = useScamCheckMutation()
  const imageMutation = useScamCheckImageMutation()
  const videoMutation = useScamCheckVideoMutation()
  const [request, setRequest] = useState<PendingScamCheckRequest | null>(null)
  const [submittedImageUrl, setSubmittedImageUrl] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [completedStep, setCompletedStep] = useState(0)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [apiDone, setApiDone] = useState(false)
  const [resultId, setResultId] = useState<string | null>(null)
  const startedRef = useRef(false)

  useEffect(() => {
    if (!requestId) {
      setErrorMessage('We could not find a pending check request.')
      return
    }
    const pendingRequest = getPendingScamCheck(requestId)
    if (!pendingRequest) {
      setErrorMessage('This check request has expired. Please submit it again.')
      return
    }
    setRequest(pendingRequest)
    setErrorMessage(null)
  }, [requestId])

  const steps = useMemo(() => {
    if (!request) return []
    return getSteps(request.mode, useBritishSpelling)
  }, [request, useBritishSpelling])

  useEffect(() => {
    if (!request || (request.kind !== 'image' && request.kind !== 'video')) {
      setSubmittedImageUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(request.file)
    setSubmittedImageUrl(objectUrl)
    return () => { URL.revokeObjectURL(objectUrl) }
  }, [request])

  // Timed step progression
  useEffect(() => {
    if (!steps.length || completedStep >= steps.length) return
    const timings = (request && STEP_TIMINGS[request.mode]) || DEFAULT_STEP_TIMINGS
    const delay = timings[completedStep] ?? 2000
    const timer = window.setTimeout(() => {
      setCompletedStep(prev => prev + 1)
    }, delay)
    return () => window.clearTimeout(timer)
  }, [completedStep, steps.length, request])

  // Fire the API call once
  useEffect(() => {
    if (!request || !steps.length || startedRef.current) return
    startedRef.current = true

    const runCheck = async () => {
      try {
        const result = request.kind === 'image'
          ? await imageMutation.mutateAsync(request.file)
          : request.kind === 'video'
            ? await videoMutation.mutateAsync(request.file)
            : await mutation.mutateAsync({
              input: request.input,
              inputType: request.inputType,
            })
        clearPendingScamCheck(request.id)
        setResultId(result.id)
        setApiDone(true)
      } catch (e) {
        setErrorMessage(checkErrorMessage(e, request.kind === 'image' || request.kind === 'video' ? 'file' : 'text'))
      }
    }

    void runCheck()
  }, [imageMutation, videoMutation, mutation, request, steps.length])

  // Navigate once both conditions are met: API done + all steps animated
  useEffect(() => {
    if (!apiDone || !resultId || completedStep < steps.length) return

    const timer = window.setTimeout(() => {
      setIsFadingOut(true)
      window.setTimeout(() => {
        router.navigate({
          to: '/tools/scam-check/$checkId',
          params: { checkId: resultId },
          search: { invitationcode: undefined, __clerk_ticket: undefined },
          replace: true,
        })
      }, 450)
    }, 600)

    return () => window.clearTimeout(timer)
  }, [apiDone, resultId, completedStep, steps.length, router])

  if (errorMessage) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-6 py-10">
          <div className="w-full p-8 text-center">
            <h1 className="text-2xl font-semibold text-slate-900">Scam check unavailable</h1>
            <p className="mt-2 text-base text-slate-600">{errorMessage}</p>
            <Link
              to="/tools"
              search={{ invitationcode: undefined, __clerk_ticket: undefined }}
              className="mt-6 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <FiArrowLeft className="h-4 w-4" />
              Back to Tools
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`min-h-screen bg-white transition-opacity duration-700 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}
    >
      <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col px-6 py-8 sm:py-12">
        <Link
          to="/tools"
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="inline-flex items-center gap-2 self-start text-sm text-slate-500 transition-colors hover:text-slate-800"
        >
          <FiArrowLeft className="h-4 w-4" />
          Cancel
        </Link>

        <section className="mt-10 flex flex-1 flex-col">
          {/* Header — for video checks the scanning preview takes the spinner's place */}
          <div className="mb-10 text-center">
            {request?.kind === 'video' && submittedImageUrl ? (
              <VideoScanPreview src={submittedImageUrl} />
            ) : (
              <BrandedSpinner size="xl" className="mx-auto" />
            )}
            <h1 className="mt-6 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
              {useBritishSpelling ? 'Analysing' : 'Analyzing'}...
            </h1>
            <p className="mt-3 text-lg text-slate-500">
              {request?.kind === 'video' ? 'Videos can take up to a minute.' : 'This usually takes a few seconds.'}
            </p>
          </div>

          {/* Submitted content preview */}
          {request?.kind === 'image' && submittedImageUrl ? (
            <div className="mb-8 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <img
                src={submittedImageUrl}
                alt="Submitted image preview"
                className="h-[160px] w-full rounded-lg object-contain"
              />
            </div>
          ) : request?.kind === 'text' && request.input ? (
            <div className="mb-8 rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
              <p className="line-clamp-3 break-words text-base text-slate-600">{request.input}</p>
            </div>
          ) : null}

          {/* Steps */}
          <div className="space-y-0">
            {steps.map((step, index) => {
              const isDone = index < completedStep
              const isCurrent = index === completedStep && completedStep < steps.length
              const isPending = index > completedStep

              return (
                <div
                  key={step.title}
                  className={`flex items-center gap-5 border-b border-slate-100 py-5 transition-opacity duration-500 ${
                    isPending ? 'opacity-30' : 'opacity-100'
                  }`}
                >
                  {/* Status indicator */}
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center">
                    {isDone ? (
                      <AnimatedCheck />
                    ) : isCurrent ? (
                      <SpinningGear />
                    ) : (
                      <div className="h-2.5 w-2.5 rounded-full bg-slate-200" />
                    )}
                  </div>

                  {/* Text */}
                  <div className="min-w-0 flex-1">
                    <p className={`text-lg font-semibold ${
                      isDone || isCurrent ? 'text-slate-900' : 'text-slate-400'
                    }`}>
                      {step.title}
                    </p>
                    <p className={`mt-0.5 text-base ${
                      isDone || isCurrent ? 'text-slate-500' : 'text-slate-300'
                    }`}>
                      {step.subtitle}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      </div>
    </div>
  )
}
